import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { toast } from '../lib/toast';
import { broadcastVideosRefresh } from './useVideos';
import type { Mp4Preset, Video, VideoJob, VideoMp4s } from '../types/video';

const POLL_MS = 2000;
/** How long "Download starting…" stays up: the browser shows the download itself from there. */
const DOWNLOAD_NOTICE_MS = 3000;

const isActive = (job?: VideoJob | null) => job?.status === 'queued' || job?.status === 'running';

/** Toast id for the end of a Save as MP4: the editor and the library tile may both report it. */
export const saveAsMp4ToastId = (videoId: string) => `save-as-mp4-${videoId}`;

/** File format of a video, for its label (from the stored type, else the file name). */
export const videoFormat = (video: Video): 'MP4' | 'WebM' | null => {
  const type = String(video.mimeType || '').toLowerCase();
  const file = String(video.fileUrl || video.file_url || video.url || '').split('?')[0].toLowerCase();
  if (type.includes('mp4') || file.endsWith('.mp4')) return 'MP4';
  if (type.includes('webm') || file.endsWith('.webm')) return 'WebM';
  return null;
};

/** The file downloads under the video's title (the server sets it on the file). */
const download = (url: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Where an MP4 request is, for the tile and the menu:
 * preparing (asking the server) → converting (server job, with progress) → downloading → idle.
 */
export type Mp4Phase = 'idle' | 'preparing' | 'converting' | 'downloading';

/**
 * MP4s of one video, for its owner (`enabled`; others see nothing of it):
 *  - Download as MP4 in a quality preset: converted once on the server (progress is followed
 *    here), then downloaded; later requests download the same file at once.
 *  - Save as MP4 (started by the editor): followed here too; the library refreshes when done.
 */
export function useMp4Download(video: Video, enabled: boolean) {
  const { t } = useTranslation();
  const [job, setJob] = useState<VideoJob | null>(video.job ?? null);
  const [mp4s, setMp4s] = useState<VideoMp4s>(video.mp4s ?? {});
  const [preparing, setPreparing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // The preset this tab asked for: downloads when it is ready.
  const wanted = useRef<Mp4Preset | null>(null);
  // A job seen running here: its end is announced (not one that ended before the page opened).
  const followed = useRef<string | null>(null);
  const noticeTimer = useRef(0);

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  // A refreshed library brings newer state.
  useEffect(() => {
    if (video.job) setJob(video.job);
    if (video.mp4s) setMp4s(video.mp4s);
  }, [video.job?.updatedAt, video.job?.status, JSON.stringify(video.mp4s ?? {})]); // eslint-disable-line react-hooks/exhaustive-deps

  const startDownload = useCallback((url: string) => {
    download(url);
    setDownloading(true);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setDownloading(false), DOWNLOAD_NOTICE_MS);
  }, []);

  const apply = useCallback((next: { job: VideoJob | null; mp4s: VideoMp4s }) => {
    setJob(next.job);
    setMp4s(next.mp4s ?? {});
    const ready = wanted.current ? next.mp4s?.[wanted.current] : undefined;
    if (ready) {
      wanted.current = null;
      toast.success(t('mp4.ready', 'MP4 ready — downloading.'));
      startDownload(ready.url);
    }
    const j = next.job;
    if (!j || isActive(j)) {
      if (j) followed.current = j.id;
      return;
    }
    if (followed.current !== j.id) return;
    followed.current = null;
    if (j.status === 'failed') {
      wanted.current = null;
      if (j.replace) toast.error(t('mp4.save_failed', 'Could not save the video as MP4. It stays as WebM.'), { id: saveAsMp4ToastId(video.id) });
      else toast.error(t('mp4.failed', 'Could not convert the video to MP4. Try again.'));
    } else if (j.replace) {
      toast.success(t('mp4.saved', 'Saved as MP4.'), { id: saveAsMp4ToastId(video.id) });
      broadcastVideosRefresh();
    }
  }, [t, startDownload, video.id]);

  // Follow a running job (also one started earlier, in the editor or in another tab).
  const converting = enabled && isActive(job);
  useEffect(() => {
    if (!converting) return;
    if (job) followed.current = job.id;
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      const { data } = await apiClient.getVideoJob(video.id);
      if (stopped) return;
      if (data) apply(data);
      timer = window.setTimeout(poll, POLL_MS);
    };
    // First look soon: short videos convert in a couple of seconds.
    timer = window.setTimeout(poll, 1000);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [converting, video.id, apply]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Download as MP4 in `preset`: the server returns the ready file at once, or converts it. */
  const start = useCallback(async (preset: Mp4Preset) => {
    if (preparing || converting) return;
    wanted.current = preset;
    setPreparing(true);
    const { data, error } = await apiClient.startVideoJob(video.id, 'mp4', { preset }).finally(() => setPreparing(false));
    if (error || !data) {
      wanted.current = null;
      const code = (error as { code?: string } | null)?.code;
      toast.error(code === 'STORAGE_EXCEEDED'
        ? t('mp4.no_storage', 'Not enough storage for this MP4. Free up space or choose a smaller quality.')
        : error?.message || t('mp4.failed', 'Could not convert the video to MP4. Try again.'));
      return;
    }
    const ready = data.mp4s?.[preset];
    if (ready) {
      wanted.current = null;
      setJob(data.job);
      setMp4s(data.mp4s);
      toast.success(t('mp4.downloading', 'Downloading MP4…'));
      startDownload(ready.url);
      return;
    }
    if (data.job && isActive(data.job) && data.job.preset !== preset) {
      // Another conversion of this video is running: one at a time.
      wanted.current = null;
      toast.info(t('mp4.busy', 'This video is already being converted. Try again when it finishes.'));
    } else {
      toast.info(t('mp4.started', 'Converting to MP4… it downloads when ready.'));
    }
    apply(data);
  }, [preparing, converting, video.id, apply, startDownload, t]);

  const phase: Mp4Phase = preparing ? 'preparing' : converting ? 'converting' : downloading ? 'downloading' : 'idle';
  return { phase, busy: phase !== 'idle', job, mp4s, progress: job?.progress ?? 0, start };
}
